import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { showAlert } from 'state/actions/site';
import useAgencyFanOutQueries from './useAgencyFanOutQueries';
import {
  agencyAppointmentsKeys,
  hasUntappedAgentsEndpoint,
  parseUntappedAgents,
  untappedAgentsRequest
} from './api/agencyAppointmentsZoho';

// module level so the fan-out entries stay referentially stable across renders
function untappedEntry({ agencyId }) {
  return {
    queryKey: agencyAppointmentsKeys.untapped(agencyId),
    request: untappedAgentsRequest(agencyId),
    parse: parseUntappedAgents
  };
}

/**
 * loads the agents with no active appointment and no open request for every applied
 * agency, merged into one map keyed by `agencyId::agentId`.
 * @param agencies - list of { agencyId, agencyName } currently applied
 */
export default function useUntappedAgents(agencies) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const fan = useAgencyFanOutQueries(agencies, untappedEntry, {
    enabled: hasUntappedAgentsEndpoint()
  });

  const untappedAgents = useMemo(() => {
    const merged = {};
    for (const { agencyId, agencyName, data } of fan.results) {
      for (const [agentId, agent] of Object.entries(data ?? {})) {
        merged[`${agencyId}::${agentId}`] = { ...agent, agencyId, agencyName };
      }
    }
    return merged;
  }, [fan.results]);

  // alert once per set of failures, not on every render the failure survives
  const alertedRef = useRef('');
  const { failedAgencies, isFetching } = fan;
  useEffect(() => {
    if (isFetching) return;
    const key = failedAgencies.map(a => a.agencyId).join(',');
    if (key === alertedRef.current) return;
    alertedRef.current = key;
    if (failedAgencies.length === 0) return;
    const names = failedAgencies
      .map(a => a.agencyName || a.agencyId)
      .join(', ');
    dispatch(
      showAlert({
        message: `Failed to load untapped agents for: ${names}`,
        code: 'error'
      })
    );
  }, [failedAgencies, isFetching, dispatch]);

  // drops a suggested agent straight out of that agency's cached list, so the card
  // stays gone across remounts and tab switches instead of only for this mount
  const removeUntappedAgent = useCallback(
    (agentId, agencyId) => {
      const agentStr = String(agentId ?? '').trim();
      if (!agentStr || !agencyId) return;
      queryClient.setQueryData(
        agencyAppointmentsKeys.untapped(agencyId),
        current => {
          if (!current?.[agentStr]) return current;
          const next = { ...current };
          delete next[agentStr];
          return next;
        }
      );
    },
    [queryClient]
  );

  return {
    untappedAgents,
    loadingUntappedAgents: fan.isLoading,
    failedAgencies: fan.failedAgencies,
    retryFailed: fan.retryFailed,
    removeUntappedAgent
  };
}
